import { HookForm, Button, FormTextField, Stack, FormContentIsland } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useCallback } from 'react';
import { useFormError } from '../../../../hooks/useFormError';
import { ApiCreateWebhookRequest, ApiError } from '../../../../openapi';
import { UseMutationResult } from '@tanstack/react-query';
import { WebhookEventsField } from './WebhookEventsField';

type FormValues = ApiCreateWebhookRequest;

export function WebhookForm({
  mutation,
  defaultValues,
}: {
  mutation: UseMutationResult<unknown, ApiError, FormValues>;
  defaultValues?: FormValues;
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(defaultValues);

  const form = useForm<FormValues>({
    defaultValues: {
      uri: defaultValues?.uri,
      secret: defaultValues?.secret,
      events: defaultValues?.events ?? {},
    },
  });

  useFormError(form, 'uri', mutation.error);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (!mutation.isPending) {
        mutation.mutate(values);
      }
    },
    [mutation],
  );

  return (
    <Stack spacing={4} component={HookForm} form={form} onSubmit={handleSubmit} width="100%">
      <FormContentIsland>
        <FormTextField
          name="uri"
          label={t('Webhook URL')}
          placeholder="https://example.com/webhook"
          required
          fullWidth
          autoFocus={!isEdit}
        />

        {isEdit ? (
          <>
            <FormTextField
              name="secret"
              label={t('Secret')}
              placeholder={t('Used for webhook signature validation')}
              helperText={t('If you leave this field empty, a random secret will be generated')}
              fullWidth
            />
            <WebhookEventsField
              name="events"
              initialValue={defaultValues?.events ?? {}}
              label={t('Events')}
            />
          </>
        ) : null}
      </FormContentIsland>
      <Button
        type="submit"
        loading={mutation.isPending}
        disabled={!form.formState.isDirty}
        fullWidth
        size="large"
      >
        {isEdit ? t('Update') : t('Create')}
      </Button>
    </Stack>
  );
}
